import { useCallback } from 'react';

import { getInstitutionKey } from './useConnectStatementsData';
import useAccountingSetupConnectorId from './useAccountingSetupConnectorId';

import useQuilttSessionToken from 'services/quiltt/queries/useQuilttSessionToken';
import { useCachedResolvabilityResults } from 'services/quiltt/utils/quilttResolvability';
import InstitutionResolvabilityCheckers from 'services/quiltt/utils/InstitutionResolvabilityCheckers';

interface EligibilityAccount {
    institution_id?: string;
    institution_name: string;
}

interface AutoStatementsEligibilityWarmersProps {
    accounts: EligibilityAccount[];
}

/**
 * Warms the connector-keyed resolvability cache for the given accounts' institutions (one
 * error-tolerant checker per unique uncached institution). Mount during account classification so
 * results are usually settled by the time the banking step completes. Renders nothing visible.
 */
export function AutoStatementsEligibilityWarmers({ accounts }: Readonly<AutoStatementsEligibilityWarmersProps>) {
    const { data: sessionToken } = useQuilttSessionToken();
    const connectorId = useAccountingSetupConnectorId();

    if (!sessionToken?.token || !connectorId) {
        return null;
    }

    return (
        <InstitutionResolvabilityCheckers
            institutions={accounts.map((account) => ({
                institutionId: getInstitutionKey(account),
                institutionName: account.institution_name,
            }))}
            connectorId={connectorId}
            token={sessionToken.token}
        />
    );
}

/**
 * Computes Auto Statements eligibility from the resolvability cache. `getEligibility` returns `false`
 * only when every business institution has a settled not-resolvable result — AccountingSetup then
 * hides and auto-skips the wizard step. Anything unsettled or resolvable returns `undefined` (show the
 * step; the step-level fallback decides once the member lands on it).
 */
export default function useAutoStatementsEligibility() {
    const connectorId = useAccountingSetupConnectorId();
    const { results } = useCachedResolvabilityResults(connectorId);

    const getEligibility = useCallback(
        (businessAccounts: EligibilityAccount[]): false | undefined => {
            if (businessAccounts.length === 0) {
                return undefined;
            }
            const keys = businessAccounts.map(getInstitutionKey);
            const allSettledIneligible = keys.every((key) => results[key] === false);
            return allSettledIneligible ? false : undefined;
        },
        [results]
    );

    return { getEligibility };
}
